<script setup lang="ts">
import { ref, onMounted } from "vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import { useFormValidation } from "../../../../composables/useFormValidation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { required } from "../../../../utils/validation";
import {
  Item,
} from "../../../../types/models";
import MessageBox from "../../../../services/MessageBox";
import ModalFormComponent from "../../../../components/ui/common/ModalFormComponent.vue";
import DocumentHeader from "../../../../components/ui/Templates/DocumentHeader.vue";
import DocumentFooter from "../../../../components/ui/Templates/DocumentFooter.vue";
import Buttons from "../../../../components/ui/form/Buttons.vue";
import FileUpload from "../../../../components/ui/form/FileUpload.vue";
import * as XLSX from "xlsx";
import { TableField } from "../../../../components/table/table";
import ItemPurchaseGrid from "../../../../components/ui/Templates/ItemPurchaseGrid.vue";
import IconComponent from "../../../../components/ui/common/IconComponent.vue";

const props = defineProps<{
  endPoint: string;
}>();

interface matchField {
  field_name: string;
  caption: string;
  requried: boolean;
  mapped?: string;
}

interface excelFields {
  id: string;
  name: string;
}

const {
  fields,
  setFieldRules,
  validateForm,
} = useFormValidation({
  price_lists_id: "",
  warehouses_id: "",
  currencys_id: "",
  exchange_rate: 1,
  outlets_id: "",
});
//setFieldRules("price_lists_id", [required("Select Price List")]);
setFieldRules("warehouses_id", [required("Select Warehouse")]);
//setFieldRules("currencys_id", [required("Select Currency")]);
//setFieldRules("exchange_rate", [required("Select Exchange Rate")]);

const matchFields = ref<matchField[]>([
  { field_name: "ref_no", caption: "Ref No", requried: true, mapped: "" },
  { field_name: "prefix", caption: "Prefix", requried: true, mapped: "" },
  { field_name: "date_time", caption: "Date", requried: true, mapped: "" },
  { field_name: "vendor", caption: "Vendor", requried: true, mapped: "" },
  {
    field_name: "currency",
    caption: "Currency",
    requried: true,
    mapped: "",
  },
  {
    field_name: "exchange_rate",
    caption: "Exchange Rate",
    requried: true,
    mapped: "",
  },
  {
    field_name: "vendor_reference",
    caption: "Vendor Reference",
    requried: false,
    mapped: "",
  },
  {
    field_name: "remarks",
    caption: "Remarks",
    requried: false,
    mapped: "",
  },
  { field_name: "product_code", caption: "Product Code", requried: true, mapped: "" },
  { field_name: "quantity", caption: "Quantity", requried: true, mapped: "" },
  { field_name: "price", caption: "Price", requried: true, mapped: "" },  
  { field_name: "discount_a", caption: "Discount Amount", requried: false, mapped: "" },  
  { field_name: "tax_detail", caption: "Tax Code", requried: true, mapped: "" },
  { field_name: "tax_rate", caption: "Tax Rate", requried: true, mapped: "" },
  { field_name: "tax_amount", caption: "Tax Amount", requried: false, mapped: "" },
  { field_name: "line_total", caption: "Line Total", requried: true, mapped: "" },  
]);

const tableColumns = ref<TableField[]>([]);

const excelData = ref<any[][]>([]);
const colHeaders = ref<excelFields[]>([]);

const handleFileUpload = (files: any[]) => {
  console.log(files);
  const file = files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    if (e.target && e.target.result) {
      const data = new Uint8Array(e.target.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0]; // Get the first sheet name
      const sheet = workbook.Sheets[sheetName];
    let jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 }); // Convert sheet to JSON

    // Format date columns in the first row (headers)
    const dateColIndexes = (jsonData[0] as string[])
      .map((header: string, idx: number) =>
        header.toLowerCase().includes("date") ? idx : -1
      )
      .filter(idx => idx !== -1);

    // Format date values in each row (except header)
    jsonData = (jsonData as any[][]).map((row: any[], rowIdx: number) => {
      if (rowIdx === 0) return row;
      dateColIndexes.forEach(colIdx => {
        const val = row[colIdx];        
        if (val && !isNaN(Date.parse(val)) && typeof val !== "number") {
          // If already a valid date string, format it
          row[colIdx] = new Date(val).toISOString().slice(0, 10);
        } else if (typeof val === "number") {
          // Excel dates are numbers, convert using XLSX
          row[colIdx] = XLSX.SSF.format("yyyy-mm-dd", val);
        }
      });
      return row;
    });
    if (jsonData.length > 0) {
      const arr = (jsonData[0] as string[]).map(
        (text: string, index: number) => ({
        id: index.toString(),
        name: text,
        })
      );
      arr.forEach((element) => {
        colHeaders.value.push(element);
        tableColumns.value.push({
        name: element.name,
        caption: element.name,
        sortable: false,
        fieldType: element.name.toLowerCase().includes("date") ? "date" : "text",
        });
      });
      jsonData.splice(0, 1); // Remove the first row from data

      excelData.value = jsonData as any[][];
    }
    }
  };
  reader.readAsArrayBuffer(file);
};

const mappedHeaders = ref<matchField[]>([]);
interface MappedInvoiceRow {
    ref_no: string;
    prefix: string;
    document_no: string;
    date_time: string;
    vendor: string;
    currency: string;
    vendor_reference: string;
    vendors_id: string;
    product_list: any[];
    currencys_id: string;
    warehouses_id: string;
    exchange_rate: number;
    product_code: string;
    quantity: number;
    price: number;
    discount_a: number;
    tax_detail: string;
    remarks: string;
    tax_details_id: string;
    tax_rate: number;
    tax_amount: number;
    line_total: number;
    items_id: string;
    item_name: string;
    purchase_uom_id: string;
    purchase_uom_qty: string;
    valid: boolean;
    import?: boolean;
    items?: any[]; // Add this property to fix the error
    expanded?: boolean;
    error?: string;
}

const mappedData = ref<MappedInvoiceRow[]>([]);
const billsForImportData = ref<MappedInvoiceRow[]>([]);

const handlePreview = async () => {
  mappedHeaders.value = [];
  mappedData.value = [];
  dbData.value = [];
  const failed =
    matchFields.value.filter(
      (field) => field.mapped === "" && field.requried === true
    ).length > 0;
  if (failed) {
    MessageBox.show(
      "Please map all required fields before previewing the data.",
      "error"
    );
    return;
  }

    if (!validateForm()) {
    return;
  }

  const match_fields = matchFields.value.filter((field) => field.mapped !== "");
  match_fields.forEach((field) => {
    mappedHeaders.value.push(field);
  });
  mappedHeaders.value.push({
    field_name: "vendors_id",
    caption: "DB Vendor",
    requried: true,
    mapped: mappedHeaders.value.length.toString(),
  });
  console.log(mappedHeaders.value);
  excelData.value.forEach((element) => {
    let d = {
      ref_no: matchFields.value.find((field) => field.field_name === "ref_no")
        ?.mapped
        ? element[
            Number(
              matchFields.value.find((field) => field.field_name === "ref_no")
                ?.mapped
            )
          ]
        : "",
    prefix: matchFields.value.find((field) => field.field_name === "prefix")
        ?.mapped
        ? element[
            Number(
              matchFields.value.find((field) => field.field_name === "prefix")
                ?.mapped
            )
          ]
        : "",
        document_no: "",
      date_time: matchFields.value.find(
        (field) => field.field_name === "date_time"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "date_time"
              )?.mapped
            )
          ]
        : "",
      vendor: matchFields.value.find(
        (field) => field.field_name === "vendor"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find((field) => field.field_name === "vendor")
                ?.mapped
            )
          ]
        : "",
      currency: matchFields.value.find(
        (field) => field.field_name === "currency"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find((field) => field.field_name === "currency")
                ?.mapped
            )
          ]
        : "",
      exchange_rate: matchFields.value.find(
        (field) => field.field_name === "exchange_rate"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "exchange_rate"
              )?.mapped
            )
          ]
        : "",
      products: matchFields.value.find(
        (field) => field.field_name === "products"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find((field) => field.field_name === "products")
                ?.mapped
            )
          ]
        : "",
      vendor_reference: matchFields.value.find(
        (field) => field.field_name === "vendor_reference"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "vendor_reference"
              )?.mapped
            )
          ]
        : "",
        product_code: matchFields.value.find(
        (field) => field.field_name === "product_code"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "product_code"
              )?.mapped
            )
          ]
        : "",
  quantity: matchFields.value.find(
        (field) => field.field_name === "quantity"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "quantity"
              )?.mapped
            )
          ]
        : 1,
    price: matchFields.value.find(
        (field) => field.field_name === "price"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "price"
              )?.mapped
            )
          ]
        : "",
        discount_a: matchFields.value.find(
        (field) => field.field_name === "discount_a"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "discount_a"
              )?.mapped
            )
          ]
        : 0,
    tax_detail: matchFields.value.find(
        (field) => field.field_name === "tax_detail"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "tax_detail"
              )?.mapped
            )
          ]
        : "",
    tax_details_id: "",
    tax_rate: 0,
    tax_amount: matchFields.value.find(
        (field) => field.field_name === "tax_amount"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "tax_amount"
              )?.mapped
            )
          ]
        : "",
    line_total: matchFields.value.find(
        (field) => field.field_name === "line_total"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "line_total"
              )?.mapped
            )
          ]
        : "",
    remarks: matchFields.value.find(
        (field) => field.field_name === "remarks"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "remarks"
              )?.mapped
            )
          ]
        : "",
      vendors_id: "",
      product_list: [],
      currencys_id: '',
      warehouses_id: fields.value.warehouses_id.value,
      items_id: "",
      item_name: "",
        purchase_uom_id: "",
        purchase_uom_qty: "",
        valid: true
    };


    d.document_no = d.prefix + d.ref_no;

    mappedData.value.push(d);
  });


const uniqueProductCodes = Array.from(new Set(mappedData.value.map(d => d.product_code).filter(Boolean)));
const uniqueCurrencies = Array.from(new Set(mappedData.value.map(d => d.currency).filter(Boolean)));
const uniqueVendors = Array.from(new Set(mappedData.value.map(d => d.vendor).filter(Boolean)));
const uniqueTaxCodes = Array.from(new Set(mappedData.value.map(d => d.tax_detail).filter(Boolean)));

// You can use these arrays as needed, e.g. log them:
console.log('Unique Product Codes:', uniqueProductCodes);
console.log('Unique Currencies:', uniqueCurrencies);
console.log('Unique Vendors:', uniqueVendors);
console.log('Unique Tax Codes:', uniqueTaxCodes);

const data = await getListData(uniqueProductCodes, uniqueCurrencies, uniqueVendors, uniqueTaxCodes);
console.log('Data from API:', data);
mappedData.value.forEach((record) => {
    const vendor = data.vendors.find((v: any) => v.name.toLowerCase() === record.vendor.toLowerCase());
    record.vendors_id = vendor ? vendor.id : "";
    const currency = data.currencys.find((c: any) => c.code.toLowerCase() === record.currency.toLowerCase());
    record.currencys_id = currency ? currency.id : "";
    const tax = data.taxDetails.find((t: any) => t.code.toLowerCase() === record.tax_detail.toLowerCase());
    record.tax_details_id = tax ? tax.id : "";
    record.tax_rate = tax ? tax.percentage : 0;
    const product = data.items.find((p: any) => p.code.toLowerCase() === record.product_code.toLowerCase());
    if (product) {
        record.items_id = product.id;
        record.item_name = product.name;
        record.purchase_uom_id = product.purchase_uom_id;
        record.purchase_uom_qty = product.purchase_uom_qty;
        /*record.product_list = [{
            item_id: product.id,
            code: product.code,
            name: product.name,
            quantity: record.quantity || 0,
            price: record.price || 0,
            tax_rate: record.tax_rate || 0,
            tax_details_id: record.tax_details_id || "",
            discount_p: 0,
            discount_a: 0,
            sub_total: (record.quantity || 0) * (record.price || 0),
            value_before_tax: (record.quantity || 0) * (record.price || 0),
            tax_amount: ((record.quantity || 0) * (record.price || 0) * (record.tax_rate || 0)) / 100,
            net_total: ((record.quantity || 0) * (record.price || 0)) + (((record.quantity || 0) * (record.price || 0) * (record.tax_rate || 0)) / 100),
            inventory_qty: record.quantity || 0,
            purchase_uom_qty: 1,
            uoms_id: product.purchase_uom_id,
            uom_name: product.sales_uom_name,
        }];*/
    } else {
        record.product_list = [];
    }
});

mappedData.value.sort((a, b) => {
    if (a.document_no < b.document_no) return -1;
    if (a.document_no > b.document_no) return 1;
    return 0;
});

mappedData.value.forEach((record) => {
    let bill = billsForImportData.value.find(b => b.document_no === record.document_no);
    if (!bill) {
        // Clone the record and push as a new bill
        billsForImportData.value.push({ ...record, product_list: [
            {
                id: 1,
                items_id: record.items_id,
                photo: "",
                code: record.product_code,
                name: record.item_name,
                item_name: record.item_name,
                sales_name: record.item_name,
                item_types_id: "",
                tracked_by: "Quantity",
                purchase_uom: "",
                purchase_uom_qty: 1,
                sales_uom: "",
                sales_uom_qty: 1,
                default_sales_uom_id: "",
                default_sales_uom_qty: 1,
                is_package: false,
                use_package_price: false,
                is_expiry: false,
                tax_details_id: record.tax_details_id || 1,
                inventory_uom_id: "",
                inventory_uom_qty: 1,
                purchase_uom_id: record.purchase_uom_id,
                sales_uom_id: "",
                has_variants: false,
                variants: "",
                tax: "",
                purchase_order_items_id: "",
                purchase_goods_receipts_id: "",
                warehouses_id: record.warehouses_id ?? fields.value.warehouses_id.value,
                item_variants_id: "",
                unit_of_measures_id: "",
                unit_of_measure: "",
                on_hand: 0,
                quantity: record.quantity || 1,
                uom_qty: 1,
                inventory_qty: record.quantity || 1,
                cost: record.price || 0,
                price: 0,
                selling_price: 0,
                original_price: 0,
                sub_total: (record.quantity || 1) * (record.price || 0),
                discount_p: 0,
                discount_a: record.discount_a || 0,
                value_before_tax: ((record.quantity || 1) * (record.price || 0)) - (record.discount_a || 0),
                tax_rate: record.tax_rate || 0,
                tax_amount: record.tax_amount || 0,
                net_total: record.line_total,
                remarks: "",
                uid: "",
                batches: [],
                serials: [],
                packing_prices: [],
                quantity_prices: [],
                shelfs: [],
                variant_list: [],
              }
        ] });
    } else {
        // Only add product_list items to the existing bill
        if (bill.product_list && bill.product_list.length > 0) {
            // Add detailed product_list item instead of just pushing record.product_list
            if (record.items_id && record.item_name) {
              bill.product_list.push({
                id: 1,
                items_id: record.items_id,
                photo: "",
                code: record.product_code,
                name: record.item_name,
                item_name: record.item_name,
                sales_name: record.item_name,
                item_types_id: "",
                tracked_by: "Quantity",
                purchase_uom: "",
                purchase_uom_qty: 1,
                sales_uom: "",
                sales_uom_qty: 1,
                default_sales_uom_id: "",
                default_sales_uom_qty: 1,
                is_package: false,
                use_package_price: false,
                is_expiry: false,
                tax_details_id: record.tax_details_id || 1,
                inventory_uom_id: "",
                inventory_uom_qty: 1,
                purchase_uom_id: record.purchase_uom_id,
                sales_uom_id: "",
                has_variants: false,
                variants: "",
                tax: "",
                purchase_order_items_id: "",
                purchase_goods_receipts_id: "",
                warehouses_id: record.warehouses_id ?? fields.value.warehouses_id.value,
                item_variants_id: "",
                unit_of_measures_id: "",
                unit_of_measure: "",
                on_hand: 0,
                quantity: record.quantity || 1,
                uom_qty: 1,
                inventory_qty: record.quantity || 1,
                cost: record.price || 0,
                price: 0,
                selling_price: 0,
                original_price: 0,
                sub_total: (record.quantity || 1) * (record.price || 0),
                discount_p: 0,
                discount_a: record.discount_a || 0,
                value_before_tax: ((record.quantity || 1) * (record.price || 0)) - (record.discount_a || 0),
                tax_rate: record.tax_rate || 0,
                tax_amount: record.tax_amount || 0,
                net_total: record.line_total,
                remarks: "",
                uid: "",
                batches: [],
                serials: [],
                packing_prices: [],
                quantity_prices: [],
                shelfs: [],
                variant_list: [],
              });
            }
        }
    }
});

console.log('bills for import', billsForImportData.value);

  handleGetDataFromDb();

  console.log(mappedData.value);
};

const getListData = async (productCodes: any, currencys: any, Vendors: any, taxCodes: any) => {
// Call API and return the data
const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") + "data-for-import",
    {
        codes: productCodes,
        currencys: currencys,
        vendors: Vendors,
        taxCodes: taxCodes,
        warehouses_id: fields.value.warehouses_id.value,
    }
);
return response.data;
};

const getBgColor = (row: any) => {
  if (!row.ref_no || row.ref_no == "") {
    return "#ed8888";
  }
  if (!row.date_time || row.date_time == "") {
    return "#ed8888";
  }
  if (!row.vendors_id || row.vendors_id == "") {
    return "#ed8888";
  }
  if (!row.product_list || row.product_list.length <= 0) {
    return "#ed8888";
  }
  if (!row.currencys_id || row.currencys_id == "") {
    return "#ed8888";
  }
  if (!row.exchange_rate || row.exchange_rate <= 0) {
    return "#ed8888";
  }
  if (!row.tax_details_id || row.tax_details_id == "") {
    return "#ed8888";
  }
  return "";
};

const validated = ref(false);
const handleValidate = () => {
  let valid = true;
  billsForImportData.value.forEach((element, index) => {
    if (element.ref_no == "") {
      valid = false;
      //return false;
    }
    if (element.date_time == "") {
      valid = false;
      //return false;
    }
    if (element.vendors_id == "") {
      valid = false;
      //return false;
    }
    if (element.product_list.length <= 0) {
      valid = false;
      //return false;
    }
    if (element.currencys_id == "") {
      valid = false;
      //return false;
    }
    if (element.exchange_rate <= 0) {
      valid = false;
      return false;
    }
    if (element.purchase_uom_id == "") {
      valid = false;
      //return false;
    }
    if (element.tax_details_id == "") {
      valid = false;
      //return false;
    }
     if (element.warehouses_id == "") {
      valid = false;
      //return false;
    }

    billsForImportData.value[index].valid = valid;
    if (index == billsForImportData.value.length - 1) {
      validated.value = valid;
    }
  });


  console.log("Validated", validated.value);
    if (!valid) {
        MessageBox.show(
        "Please fix all invalid records before validating.",
        "error"
        );
    } else {
        MessageBox.show("All records are valid.", "info");
    }
};


const handleImportPost = async () => {
    let param =billsForImportData.value[importIndex.value]
    param.items = param.product_list;
  await api.post(
        (props.endPoint.slice(-1) == "/"
          ? props.endPoint
          : props.endPoint + "/") + "create",
          param,  {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      hideError: true
    }
      ).then((response) => {
        if (response.success == true) {
          Toast.show(
            "Importing record " +
              (importIndex.value + 1) +
              " of " +
              billsForImportData.value.length +
              " records",
            "info"
          );
          if (importIndex.value < billsForImportData.value.length - 1) {
            importIndex.value++;
            handleImportPost();
          } else {
            const failedCount = billsForImportData.value.filter(bill => bill.import === false).length;
            if (failedCount > 0) {
              MessageBox.show(
                `${billsForImportData.value.length - failedCount} records imported successfully. ${failedCount} records failed to import.`,
                "error"
              );
            } else {
              Toast.show("All records imported successfully", "info");
            }   
            validated.value = false;
            //billsForImportData.value = [];
            mappedData.value = [];
          }
        } else {
            billsForImportData.value[importIndex.value].import = false;
            billsForImportData.value[importIndex.value].error = response.message || "Error importing record";
            if (importIndex.value < billsForImportData.value.length - 1) {
                importIndex.value++;
                handleImportPost();
            }
          Toast.show(
            "Error importing record " +
              (importIndex.value + 1) +
              " of " +
              billsForImportData.value.length +
              " records",
            "error"
          );
        }
      })
      .catch((error => {
        console.log(error);
         billsForImportData.value[importIndex.value].import = false;
         billsForImportData.value[importIndex.value].error = error.response.message || "Error importing record";
        Toast.show(
          "Error importing record " +
            (importIndex.value + 1) +
            " of " +
            billsForImportData.value.length +
            " records",
          "error"
        );
      }));
};

const updateLineTotal = (item: Item)  => {
  item.inventory_qty = (item.quantity || 1) * (item.purchase_uom_qty || 1);
  item.sub_total = (item.quantity || 1) * (item.cost || 0);
  item.discount_a = Math.round((item.sub_total * item.discount_p || 0) / 100);
  item.value_before_tax = item.sub_total - item.discount_a;
  item.tax_amount =
    ((item.sub_total - item.discount_a) * (item.tax_rate || 0)) / 100;
  item.net_total = item.sub_total - item.discount_a + item.tax_amount;
  console.log('updateLineTotal', item);
}

const importIndex = ref(0);
const handleImport = async () => {
  
  billsForImportData.value.forEach(async (element) => {
    element.items = element.product_list;
    element.import = true;
    element.items.forEach((item: Item) => {
        updateLineTotal(item)
    });    
  });
  if (dbData.value.filter((record) => getBgColor(record) != "").length > 0) {
    MessageBox.show(
      "Please fix all invalid records before importing.",
      "error"
    );
    return;
  } else {
  console.log('start posting', dbData.value[0]);
  importIndex.value = 0;
  handleImportPost()
  }
};

interface DbRow {
  ref_no: string;
  date_time: string;
  customers_id: string;
  product_list: Item[];
  credit_terms_id: string;
  price_lists_id: string;
  warehouses_id: string;
  [key: string]: any; // Add this to allow additional properties
}

const dbData = ref<DbRow[]>([]);
const handleGetDataFromDb = async () => {
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "map-excel",
    {
      data: mappedData.value,
      price_lists_id: fields.value.price_lists_id.value,
      warehouses_id: fields.value.warehouses_id.value,
      currencys_id: fields.value.currencys_id.value,
      exchange_rate: fields.value.exchange_rate.value,
    }
  );
  if (response.success == true) {
    dbData.value = response.data;
  }
};

onMounted(() => {});


function toggleItemExpand(index: Number) {
  billsForImportData.value[index as number].expanded = !billsForImportData.value[index as number].expanded;
}


</script>

<template>
  <ModalFormComponent width="100%" @close="$emit('close')">
    <template #header>
      <!-- Header -->
      <DocumentHeader
        title="Import using Excel Files"
        end-point=""
        :readonly="true"
        :show-drop-down="false"
        @close="$emit('close')"
        :permissions="[]"
        :show-share-icons="false"
        :show-save-icon="false"
      >
        <template>
          <Buttons caption="Import" icon="Import" @click="$emit('import')" />
        </template>
      </DocumentHeader>
    </template>

    <template #form>
      <div class="grid grid-cols-5 gap-6 mb-8">
        <div>
          <FileUpload
            label="Excel File"
            accept=".xlsx, .xls"
            @upload-success="handleFileUpload"
            path=""
            :local="true"
          />
          <div class="grid grid-cols-2 gap-2 mt-2">            
            <SearchSelect
              label="Warehouse"
              end-point="system/company/warehouses"
              class="col-span-2"
              v-model="fields.warehouses_id.value"
              :error="
                fields.warehouses_id.touched ? fields.warehouses_id.error : null
              "
              @blur="fields.warehouses_id.touched = true"
            />

            <Buttons
              caption="Prepare Data"
              icon="ListChecks"
              @click="handlePreview"
              className="mt-2"
            />
            <Buttons
              caption="Validate"
              icon="ShieldCheck"
              v-if="billsForImportData.length > 0"
              @click="handleValidate"
              className="mt-2"
            />

            <Buttons
              caption="Import"
              icon="Import"
              class="col-span-2"
              type="info"
              v-if="validated"
              @click="handleImport"
              className="mt-2"
            />
          </div>
        </div>
        <div class="col-span-4 grid grid-cols-5 gap-1">
          <div v-for="(field, index) in matchFields" :key="index">
            <SearchSelect
              :label="field.caption"
              :options="colHeaders"
              :multiple="false"
              :placeholder="'Select Excel Fields'"
              v-model="field.mapped"
            />
          </div>
        </div>
      </div>
      <div v-if=" billsForImportData.length > 0">
        Total {{ billsForImportData.length }} records,
        <span
          >{{
            billsForImportData.filter((record) => getBgColor(record) != "").length
          }}
          Invalid Records</span
        >
        <table
          class="divide-y divide-gray-200 dark:divide-gray-700"
          id="table-records"
        >
          <thead>
            <tr id="table-headers">
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Ref No
              </th>
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Date
              </th>
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Vendor
              </th>
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Currency
              </th>
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Exch Rate
              </th>
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Tax Definition
              </th>              
              <!--<th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Delivery Address
              </th>-->
            </tr>
          </thead>
          <tbody
            class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700"
          >
            <template v-for="(row, index) in billsForImportData" :key="index">
              <tr
                class="hover:bg-gray dark:hover:bg-gray-700"
                :style="'background-color: ' + getBgColor(row)"
              >
                <td
                  class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                >
                <div class="flex items-center">
                    <IconComponent name="CircleCheckBig" class="text-green-500 mr-2" v-if="row.import"/>
                    <IconComponent name="CircleX" class="text-red-500 mr-2" v-if="!row.import"/>
                    <button
                      @click="toggleItemExpand(index)"
                      class="mr-2"
                      v-if="
                        (row.product_list ?? []).length > 0
                      "
                    >
                      <IconComponent
                        :name="row.expanded ? 'ArrowUp' : 'ArrowDown'"
                      />
                    </button>
                  {{ row.ref_no }}
                  </div>
                </td>
                <td
                  class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                >
                  {{ row.date_time }}
                </td>
                <td class="px-6 py-4 text-sm text-gray-900 dark:text-white">
                  <span >{{ row.vendor }}</span>
                  <SearchSelect
                    v-if="row.vendors_id === ''"
                    label=""
                    :remote="true"
                    palceholder="Select Vendor"
                    end-point="procurement/master-data/vendors"
                    v-model="row.vendors_id"
                  />
                </td>
                <td class="px-6 py-4 text-sm text-gray-900 dark:text-white">
                  <span v-if="row.currency">{{ row.currency }}</span>
                  <SearchSelect
                    v-else
                    label=""
                    palceholder="Select Currency"
                    end-point="finance/master-data/currency"
                    v-model="row.currencys_id"
                  />
                </td>
                <td
                  class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                >
                  {{ row.exchange_rate }}
                </td>
                <td class="px-6 py-4 text-sm text-gray-900 dark:text-white">
                  <span v-if="row.tax_details_id">{{ row.tax_detail }}</span>
                  <SearchSelect
                    v-else
                    label=""
                    placeholder="Select Price List"
                    @select="
                      (item) => {
                        row.tax_details_id = item.id;
                        row.tax_detail = item.name;
                      }
                    "
                    end-point="finance/master-data/price-lists"
                    v-model="row.tax_details_id"
                  />
                </td>
              </tr>
              <tr v-if="row.expanded" class="bg-gray-200 dark:bg-gray-900">
                <td
                  colspan="11"
                  class="px-6 py-4 text-sm text-gray-900 dark:text-white"
                >
                  <ItemPurchaseGrid
                    :items="row.product_list ?? []"
                    :exchange_rate="row.exchange_rate || 1"
                    :readonly="true"
                    :allow-new-row="false"
                    :show-actions="false"
                    :show-serial="false"
                    :show-qty="false"
                    :show-price="false"
                    :show-discount="false"
                    :show-tax="false"
                    :show-total="false"
                    :show-amount="false"
                  />
                </td>
              </tr>
              <tr v-if="row.error" class="bg-red-200 dark:bg-red-900">
                <td
                  colspan="11"
                  class="px-6 py-4 text-sm text-white9400 dark:text-white"
                >
                {{ row.error }}
                </td>
                </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div v-else>
        <table
          class="divide-y divide-gray-200 dark:divide-gray-700"
          id="table-records"
        >
          <thead>
            <tr id="table-headers">
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                v-for="h in colHeaders"
              >
                {{ h.name }}
              </th>
            </tr>
          </thead>
          <tbody
            class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700"
          >
            <template v-for="(row, eIndex) in excelData" :key="eIndex">
              <tr class="hover:bg-gray dark:hover:bg-gray-700" :id="'row-' + eIndex">
                <td
                  class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                  v-for="(cell, cellIndex) in row"
                  :key="cellIndex"
                >
                  {{ cell }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Footer -->
    <template #footer>
      <DocumentFooter
        :readonly="true"
        end-point="endPoint"
        :permissions="[]"
        title="Import using Excel Files"
        @close="$emit('close')"
        :show-new-icon="false"
        :show-share-icons="false"
        :show-save-icon="false"
      />
    </template>
  </ModalFormComponent>
</template>
